import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { PageAdapter, PageRequest, PageResult } from './pageContract';
import { validatePageFilters } from './pageFilters';

export type { PageRequest } from './pageContract';

export type PageBridge = {
  reply?: (input: { requestId: string; connectionId: string; result: unknown }) => Promise<void>;
  pageId: string;
  title: string;
  connectionId: string;
  execute: (request: PageRequest) => Promise<PageResult>;
};
export const PageBridgeRegistration = createContext<Dispatch<SetStateAction<PageBridge | null>> | null>(null);

export function usePageBridge(adapter: PageAdapter, active = true): PageBridge {
  const register = useContext(PageBridgeRegistration);
  const [connectionId] = useState(() => crypto.randomUUID());
  const [commit, setCommit] = useState(0);
  const fingerprint = JSON.stringify(adapter.view);
  const version = useRef({ fingerprint, revision: 0 });
  if (version.current.fingerprint !== fingerprint) {
    version.current = { fingerprint, revision: version.current.revision + 1 };
  }
  const current = useRef(adapter);
  current.current = adapter;
  const pending = useRef<{
    requestId: string;
    filters: Record<string, unknown>;
    finish: (result: PageResult) => void;
  } | null>(null);
  const snapshot = () => ({
    ...current.current.view,
    version: 1 as const,
    instanceId: connectionId,
    revision: version.current.revision,
    capabilities: { set_filters: { fields: current.current.view.filterSchema, scope: 'current_view' as const } },
  });
  useEffect(() => {
    const entry = pending.current;
    if (!entry || !adapter.view.ready) return;
    if (!Object.entries(entry.filters).every(([key, value]) => adapter.view.filters[key] === value)) return;
    entry.finish({ status: 'applied', applied: true, view: snapshot() });
  }, [fingerprint, commit]);
  useEffect(
    () => () => {
      pending.current?.finish({ status: 'unknown', code: 'page_closed', error: 'The page closed before completion.' });
    },
    [],
  );
  const bridge = useMemo<PageBridge>(
    () => ({
      pageId: adapter.pageId,
      connectionId,
      get title() {
        return current.current.view.title;
      },
      execute: async (request) => {
        const reject = (code: string, error: string): PageResult => ({
          status: 'rejected',
          applied: false,
          code,
          error,
          view: snapshot(),
        });
        if (request.version !== 1 || request.pageId !== current.current.pageId || request.connectionId !== connectionId)
          return reject('stale_target', 'This request belongs to another page instance.');
        if (request.operation === 'cancel') {
          if (pending.current?.requestId === request.requestId)
            pending.current.finish({
              status: 'unknown',
              code: 'cancelled',
              error: 'Cancelled after dispatch; re-read the page before claiming an outcome.',
            });
          return { status: 'cancelled' };
        }
        if (Date.now() >= request.expiresAt) return reject('expired', 'The page request expired.');
        if (!current.current.view.ready) return reject('not_ready', 'Page data is still loading.');
        if (request.operation === 'read') return { status: 'ready', view: snapshot() };
        if (request.expectedRevision !== version.current.revision)
          return reject('stale_revision', 'The view changed. Read it again before editing.');
        if (pending.current) return reject('busy', 'Another page edit is pending.');
        const filters = request.changes?.filters;
        if (
          request.operation !== 'edit' ||
          !filters ||
          Array.isArray(filters) ||
          typeof filters !== 'object' ||
          Object.keys(request.changes ?? {}).some((key) => key !== 'filters')
        )
          return reject('invalid_operation', 'Only declared filter changes are supported.');
        return new Promise<PageResult>((resolve) => {
          const finish = (result: PageResult) => {
            clearTimeout(timer);
            pending.current = null;
            resolve(result);
          };
          const timer = setTimeout(
            () =>
              finish({
                status: 'unknown',
                code: 'settle_timeout',
                error: 'The page did not settle. Read it again to determine the outcome.',
              }),
            Math.min(15000, request.expiresAt - Date.now()),
          );
          pending.current = { requestId: request.requestId, filters, finish };
          try {
            validatePageFilters(filters, current.current.view.filterSchema);
            current.current.edit(filters);
            setCommit((value) => value + 1);
          } catch (error) {
            finish(reject('invalid_filters', error instanceof Error ? error.message : 'Filter change refused.'));
          }
        });
      },
    }),
    [adapter.pageId, connectionId],
  );
  useEffect(() => {
    if (!active || !register) return;
    register(bridge);
    return () => {
      pending.current?.finish({ status: 'unknown', code: 'page_detached', error: 'The active page changed.' });
      register((currentBridge) => (currentBridge?.connectionId === connectionId ? null : currentBridge));
    };
  }, [active, bridge, connectionId, register]);
  return bridge;
}
